import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS } from '@/lib/kbCaps';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import { CharCounter } from '@/components/shared/CharCounter';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';

const MAX_RESTRICTIONS = CAPS.restriction_items;

// F167: rotate through the 4 illustrative examples so each empty row nudges
// toward a different kind of restriction (delivery zone, minimum order, age,
// service scope) instead of repeating the same placeholder.
const PLACEHOLDER_KEYS = [
  'bot.enrich.restrictions.placeholder_1',
  'bot.enrich.restrictions.placeholder_2',
  'bot.enrich.restrictions.placeholder_3',
  'bot.enrich.restrictions.placeholder_4',
] as const;

interface RestrictionsEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialRestrictions: string[];
}

export function RestrictionsEditor({
  open,
  onOpenChange,
  initialRestrictions,
}: RestrictionsEditorProps) {
  const { t } = useTranslation();
  const { updateKbProfile } = useOnboardingState();
  const [items, setItems] = useState<string[]>(
    initialRestrictions.length > 0 ? [...initialRestrictions] : ['']
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  function updateItem(index: number, value: string) {
    setItems((prev) => prev.map((it, i) => (i === index ? value : it)));
  }

  function addItem() {
    if (items.length < MAX_RESTRICTIONS) {
      setItems((prev) => [...prev, '']);
    }
  }

  function removeItem(index: number) {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }

  // The save payload filters out blank rows, so the backend's list index (what
  // a 422 path like `restrictions.1` refers to) can differ from the UI index —
  // map UI position → payload position before looking up the error (mirrors
  // FaqEditor's payloadIndex).
  function payloadIndex(uiIndex: number): number {
    return items.slice(0, uiIndex + 1).filter((it) => it.trim()).length - 1;
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);
    try {
      const valid = items.map((it) => it.trim()).filter((it) => it);
      await updateKbProfile({ restrictions: valid });
      onOpenChange(false);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bot.enrich.restrictions.title')}</DialogTitle>
          <DialogDescription>{t('bot.enrich.restrictions.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[min(60dvh,calc(100dvh-16rem))] overflow-y-auto pr-1">
          <p className="text-xs text-muted-foreground">{t('bot.enrich.restrictions.help')}</p>
          {items.map((item, idx) => (
            <div key={idx} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor={`restriction-${idx}`}>
                  {t('bot.enrich.restrictions.item_label', { n: idx + 1 })}
                </Label>
                {items.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('bot.enrich.restrictions.remove')}
                    className={cn('h-7 w-7', HIT_AREA_44)}
                    onClick={() => removeItem(idx)}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                )}
              </div>
              <Input
                id={`restriction-${idx}`}
                value={item}
                onChange={(e) => updateItem(idx, e.target.value)}
                placeholder={t(PLACEHOLDER_KEYS[idx % PLACEHOLDER_KEYS.length])}
                maxLength={CAPS.restriction_text}
                className="min-h-11"
              />
              <CharCounter value={item} max={CAPS.restriction_text} mode="near-limit" />
              <FieldError errors={fieldErrors} path={`restrictions.${payloadIndex(idx)}`} />
            </div>
          ))}
          {items.length < MAX_RESTRICTIONS && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
              className="min-h-11 w-full"
            >
              <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
              {t('bot.enrich.restrictions.add')}
            </Button>
          )}
          <FieldError errors={fieldErrors} path="restrictions" />
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-h-11"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-11">
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
